import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaSave,
  FaTimes,
  FaUserPlus,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import { uploadImage } from "../../../../../services/userServices";

import {
  buildUserPayload,
  getRoleOptions,
  resolveMediaUrl,
} from "../utils/usersManagerUtils";

const emptyForm = {
  nom: "",
  prenom: "",
  email: "",
  password: "",
  role_id: "",
  photoURL: "",
  section_id: "",
};

export default function UserFormDrawer({
  isOpen,
  mode = "create",
  user,
  sections = [],
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const roleOptions = useMemo(() => getRoleOptions(), []);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && user) {
      setFormData({
        nom: user.nom || "",
        prenom: user.prenom || "",
        email: user.email || "",
        password: "",
        role_id: user.role_id || "",
        photoURL: user.photoURL || "",
        section_id: user.section_id || "",
      });
    } else {
      setFormData(emptyForm);
    }

    setPhotoFile(null);
    setLocalPreview("");
    setUploading(false);
    setShowPassword(false);
  }, [isOpen, isEdit, user]);

  useEffect(() => {
    if (!photoFile) return undefined;

    const url = URL.createObjectURL(photoFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const previewUrl =
    localPreview ||
    resolveMediaUrl(formData.photoURL, "/images/user_avatar.jpg");

  const title = isEdit ? "Modifier un utilisateur" : "Ajouter un utilisateur";

  const subtitle = isEdit
    ? "Mettez à jour les informations du compte, son rôle, sa section et sa photo."
    : "Créez un nouvel accès pour un responsable, un administrateur ou un compte de démonstration.";

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setPhotoFile(file);
  };

  const uploadPhotoIfNeeded = async () => {
    if (!photoFile) return formData.photoURL;

    const imageData = new FormData();
    imageData.append("image", photoFile);
    imageData.append("category", "users");

    setUploading(true);

    try {
      const uploadResponse = await uploadImage(imageData);

      return (
        uploadResponse?.filePath ||
        uploadResponse?.photoURL ||
        uploadResponse?.url ||
        uploadResponse?.path ||
        uploadResponse?.data?.filePath ||
        uploadResponse?.data?.photoURL ||
        uploadResponse?.data?.url ||
        ""
      );
    } catch (error) {
      console.error("Erreur upload image utilisateur :", error);
      toast.error("Erreur lors de l'upload de l'image.");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !formData.nom ||
      !formData.prenom ||
      !formData.email ||
      !formData.role_id
    ) {
      toast.error("Veuillez compléter les champs obligatoires.");
      return;
    }

    if (!isEdit && !formData.password) {
      toast.error("Veuillez définir un mot de passe.");
      return;
    }

    try {
      const uploadedPhotoUrl = await uploadPhotoIfNeeded();

      const payload = buildUserPayload(
        {
          ...formData,
          photoURL: uploadedPhotoUrl,
        },
        mode,
      );

      if (isEdit) {
        await onUpdate?.(user.id, payload);
      } else {
        await onCreate?.(payload);
      }
    } catch {
      // toast déjà affiché si upload en erreur
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaUserPlus />
              </span>

              <div>
                <h3 className="font-bold text-slate-950 dark:text-white">
                  Informations du compte
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Les champs nom, prénom, email et rôle sont obligatoires.
                </p>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
              <div className="grid gap-4 sm:grid-cols-2">
                <InputField
                  label="Nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                />

                <InputField
                  label="Prénom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                />

                <div className="sm:col-span-2">
                  <InputField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Mot de passe{" "}
                    {isEdit && (
                      <span className="text-slate-400">
                        (laisser vide pour ne pas modifier)
                      </span>
                    )}
                  </label>

                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-primary/40 dark:border-white/10 dark:bg-slate-950">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={!isEdit}
                      className="w-full bg-transparent text-sm text-slate-900 outline-none dark:text-white"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-slate-400 transition hover:text-primary"
                      aria-label={
                        showPassword
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Rôle
                  </label>

                  <select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">Choisir un rôle</option>
                    {roleOptions.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Section
                  </label>

                  <select
                    name="section_id"
                    value={formData.section_id}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">Aucune section</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.nom_section}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    URL de la photo
                  </label>

                  <input
                    type="text"
                    name="photoURL"
                    value={formData.photoURL}
                    onChange={handleChange}
                    placeholder="https://... ou /uploads/..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Télécharger une photo
                  </label>

                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                    <FaCloudUploadAlt />
                    {photoFile ? photoFile.name : "Choisir un fichier"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                <div className="mb-3 flex items-center gap-2">
                  <FaImage className="text-primary" />
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    Aperçu photo
                  </p>
                </div>

                <div className="aspect-square overflow-hidden rounded-[24px] bg-slate-100 dark:bg-slate-900">
                  <img
                    src={previewUrl}
                    alt="Aperçu utilisateur"
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = "/images/user_avatar.jpg";
                    }}
                  />
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Vous pouvez utiliser une URL externe ou téléverser une image.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving || uploading
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}
